import _ from "lodash";
import React from "react";
import DropZone from "react-dropzone";
import {
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import UploadIcon from "material-ui/svg-icons/file/cloud-upload";
import InsertDriveFileIcon from "material-ui/svg-icons/editor/insert-drive-file";
import DeleteIcon from "material-ui/svg-icons/action/delete";
import {CollectionTypeNames, FileItem} from "common/types/matter";
import EditableHeading from "./editable_list_heading";
import {FolderOpen} from "@material-ui/icons";

const styles = {
  uploadArea: {
    minHeight: "140px",
    margin: "0",
    textAlign: "center",
    padding: "2.5rem",
    backgroundColor: "#e3f2fd",
    cursor: "pointer",
    borderWidth: "2px",
    borderStyle: "dashed",
    borderColor: "#bdbdbd",
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  uploadAreaActive: {
    backgroundColor: "#e0f2ff",
    borderColor: "#42a5f5",
  },
  fileList: {
    marginTop: "1rem",
    border: "1px solid #bdbdbd",
    borderRadius: "4px",
    backgroundColor: "#fafafa",
  },
  fileListItem: {
    padding: "12px 16px",
    gap: "0.5em",
  },
  instructionText: {
    fontSize: "1.5rem",
    color: "#616161",
    fontWeight: 500,
    marginBottom: "12px",
  },
  subText: {
    fontSize: "1.125rem",
    color: "#9e9e9e",
  },
  fileListHeader: {
    padding: "16px 16px 8px 16px",
    color: "#616161",
    fontSize: "1.25rem",
    fontWeight: 600,
  },
  fileName: {
    color: "#424242",
    fontSize: "1.125rem",
    fontWeight: 500,
  },
  deleteButton: {
    padding: "12px",
    color: "#bdbdbd",
  },
  icon: {
    fill: "#9e9e9e",
    fontSize: "1.75rem",
  },
  uploadIcon: {
    height: "3.5rem",
    width: "3.5rem",
    fill: "#bdbdbd",
    marginBottom: "20px",
  },
  typeSelector: {
    minWidth: "180px",
    fontSize: "0.9rem",
    backgroundColor: "#ffffff",
    border: "1px solid #e0e0e0",
    borderRadius: "4px",
    padding: ".5em 1em",
    height: "32px",
    marginRight: "2em",
  },
  infoMessage: {
    padding: "0 16px 8px 16px",
    color: "#757575",
    fontSize: "0.875rem",
  },
} as const;

// Which non-MSA types the user chose to combine into one document.
export interface CombinedTypes {
  SOW: boolean;
  MSOW: boolean;
}

interface FileUploaderProps {
  fileItems: FileItem[];
  addFile: (files: File[]) => void;
  removeFile: (index: number) => void;
  handleTypeChange: (index: number, newType: string) => void;
  combinedTypes: CombinedTypes;
  toggleCombineType: (type: keyof CombinedTypes, combined: boolean) => void;
  collectionTypeNames: CollectionTypeNames;
  setCollectionTypeNames: (type: string, name: string) => void;
  hideUploadArea?: boolean;
  inWordAddin?: boolean;
  highlightedDocuments?: number[];
  allowDeleteFirstFile?: boolean;
  canUploadMsow?: boolean;
  allowedTypes?: string[];
}

interface FileListItemProps {
  fileItem: FileItem;
  index: number;
  addFile: (files: File[]) => void;
  removeFile: (index: number) => void;
  handleTypeChange: (index: number, newType: string) => void;
  isHighlighted: boolean;
  inWordAddin?: boolean;
  allowDeleteFirstFile?: boolean;
  indent: boolean;
  isLastItem?: boolean;
  canUploadMsow?: boolean;
  allowedTypes?: string[];
}

const FileListItem: React.FC<FileListItemProps> = ({
  fileItem,
  index,
  inWordAddin,
  allowDeleteFirstFile,
  removeFile,
  handleTypeChange,
  isHighlighted,
  indent,
  isLastItem,
  canUploadMsow,
  allowedTypes,
}) => {
  const typeOptions = allowedTypes ?? [
    "MSA",
    "SOW",
    ...(canUploadMsow ? ["MSOW"] : []),
  ];
  return (
    <ListItem
      key={`${fileItem.file.name}-${fileItem.file.size}-${index}`}
      style={{
        ...styles.fileListItem,
        ...(inWordAddin
          ? {
              flexWrap: "wrap",
              padding: "12px",
            }
          : {}),
        ...(indent ? {paddingLeft: "40px"} : {}),
        ...(isLastItem ? {borderBottom: "1px solid rgb(189, 189, 189)"} : 0),
      }}
    >
      <ListItemIcon style={{minWidth: inWordAddin ? "32px" : "48px"}}>
        <InsertDriveFileIcon style={styles.icon} />
      </ListItemIcon>
      <ListItemText
        primary={<span style={styles.fileName}>{fileItem.file.name}</span>}
        style={{
          flex: inWordAddin ? "1 1 120px" : "1",
          minWidth: inWordAddin ? "120px" : 0,
          marginRight: inWordAddin ? "8px" : 0,
        }}
      />
      <Select
        value={fileItem.documentType}
        onChange={event =>
          handleTypeChange(index, event.target.value as string)
        }
        displayEmpty
        style={{
          ...styles.typeSelector,
          ...(inWordAddin
            ? {
                minWidth: "120px",
                maxWidth: "160px",
                flex: "0 1 auto",
                marginRight: "8px",
              }
            : {}),
          ...(isHighlighted
            ? {
                border: "2px solid #d32f2f",
                borderRadius: "4px",
              }
            : {}),
        }}
        disableUnderline
        autoWidth={false}
        SelectDisplayProps={{
          style: {
            padding: 0,
            backgroundColor: "transparent",
            border: "none",
            outline: "none",
          },
        }}
        renderValue={selected => {
          if (!selected) {
            return (
              <span style={{color: "#9e9e9e", fontSize: "0.875rem"}}>
                Select type
              </span>
            );
          }
          return (
            <span style={{color: "#424242", fontSize: "0.875rem"}}>
              {selected}
            </span>
          );
        }}
      >
        <MenuItem value="" disabled>
          Select type
        </MenuItem>
        {typeOptions.map(type => (
          <MenuItem key={type} value={type}>
            {type}
          </MenuItem>
        ))}
      </Select>
      {!(inWordAddin && index === 0 && !allowDeleteFirstFile) && (
        <IconButton
          onClick={() => removeFile(index)}
          style={{
            ...styles.deleteButton,
            ...(inWordAddin ? {padding: "8px", marginLeft: "auto"} : {}),
          }}
          title="Remove file"
        >
          <DeleteIcon style={{fontSize: inWordAddin ? "1.25rem" : "1.5rem"}} />
        </IconButton>
      )}
    </ListItem>
  );
};

const FileUploader: React.FC<FileUploaderProps> = ({
  fileItems,
  addFile,
  removeFile,
  handleTypeChange,
  combinedTypes,
  toggleCombineType,
  collectionTypeNames,
  setCollectionTypeNames,
  hideUploadArea = false,
  inWordAddin = false,
  highlightedDocuments = [],
  allowDeleteFirstFile = false,
  canUploadMsow = false,
  allowedTypes,
}) => {
  const renderUploadArea = () => (
    <DropZone
      className="upload-area"
      key="upload-area"
      multiple={true}
      onDrop={addFile}
      style={styles.uploadArea}
      activeStyle={styles.uploadAreaActive}
    >
      <UploadIcon style={styles.uploadIcon} />
      <div style={styles.instructionText}>Upload Documents</div>
      <div style={styles.subText}>
        <span style={{textDecoration: "underline", fontWeight: 600}}>
          Choose files
        </span>
        <span> or drag and drop here</span>
      </div>
    </DropZone>
  );

  const renderFolderHeading = (heading: React.ReactNode, key: string) => (
    <ListItem
      key={key}
      style={{
        ...styles.fileListItem,
        ...(inWordAddin ? {padding: "12px"} : {}),
      }}
    >
      <ListItemIcon style={{minWidth: inWordAddin ? "32px" : "48px"}}>
        <FolderOpen style={styles.icon} />
      </ListItemIcon>
      {heading}
    </ListItem>
  );

  const renderFileList = () => {
    if (fileItems.length === 0) {
      return null;
    }

    const groupedFilesByType = _.groupBy(
      fileItems.map((item, index) => {
        return {
          ...item,
          originalIndex: index,
          isHighlighted: highlightedDocuments.includes(index),
        };
      }),
      item => item.documentType ?? "none",
    );
    const order = ["none", "MSA", "SOW", "MSOW"];

    const renderItem = (
      fileItem: typeof groupedFilesByType[string][number],
      indent: boolean,
      isLastItem: boolean,
    ) => (
      <FileListItem
        key={fileItem.id ?? `file-${fileItem.originalIndex}`}
        fileItem={fileItem}
        index={fileItem.originalIndex}
        addFile={addFile}
        removeFile={removeFile}
        handleTypeChange={handleTypeChange}
        inWordAddin={inWordAddin}
        isHighlighted={fileItem.isHighlighted}
        allowDeleteFirstFile={allowDeleteFirstFile}
        indent={indent}
        isLastItem={isLastItem}
        canUploadMsow={canUploadMsow}
        allowedTypes={allowedTypes}
      />
    );

    return (
      <div style={styles.fileList}>
        <Typography variant="h6" style={styles.fileListHeader}>
          Files ({fileItems.length})
        </Typography>
        <Typography variant="body2" style={styles.infoMessage}>
          MSA files are analysed together as one agreement. Other files are
          analysed separately unless you combine them.
        </Typography>
        <List style={{padding: 0}}>
          {order.map(type => {
            const typeFileItems = groupedFilesByType[type];
            if (!typeFileItems) {
              return null;
            }

            if (type === "none") {
              return (
                <React.Fragment key={type}>
                  <ListItem
                    style={{
                      ...styles.fileListItem,
                      ...(inWordAddin
                        ? {
                            flexWrap: "wrap",
                            padding: "12px",
                          }
                        : {}),
                    }}
                  >
                    <ListItemText
                      primary={
                        <span style={styles.fileName}>Unassigned Type</span>
                      }
                      style={{marginRight: 0}}
                    />
                  </ListItem>
                  {typeFileItems.map((fileItem, index) =>
                    renderItem(
                      fileItem,
                      false,
                      index === typeFileItems.length - 1,
                    ),
                  )}
                </React.Fragment>
              );
            }

            if (type === "MSA") {
              return (
                <React.Fragment key={type}>
                  {typeFileItems.length > 1 &&
                    renderFolderHeading(
                      <EditableHeading
                        value={collectionTypeNames[type] ?? ""}
                        onChange={name => setCollectionTypeNames(type, name)}
                        hint="These files form one agreement: the original MSA plus its amendments, analysed as one document."
                      />,
                      "MSA-heading",
                    )}
                  {typeFileItems.map(fileItem =>
                    renderItem(fileItem, typeFileItems.length > 1, false),
                  )}
                </React.Fragment>
              );
            }

            // SOW / MSOW: separate documents by default; the per-type toggle
            // combines them into one document, rendered as a folder.
            const combinableType = type as keyof CombinedTypes;
            const isCombined =
              combinedTypes[combinableType] && typeFileItems.length > 1;

            return (
              <React.Fragment key={type}>
                {typeFileItems.length > 1 && (
                  <ListItem
                    style={{
                      ...styles.fileListItem,
                      ...(inWordAddin
                        ? {
                            flexWrap: "wrap",
                            padding: "12px",
                          }
                        : {}),
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isCombined}
                          onChange={event =>
                            toggleCombineType(
                              combinableType,
                              event.target.checked,
                            )
                          }
                          color="primary"
                          size="small"
                        />
                      }
                      label={
                        <span style={{fontSize: "0.875rem", color: "#424242"}}>
                          Combine these {type} files into one document
                        </span>
                      }
                      style={{marginLeft: 0}}
                    />
                  </ListItem>
                )}
                {isCombined &&
                  renderFolderHeading(
                    <EditableHeading
                      value={collectionTypeNames[type] ?? ""}
                      onChange={name => setCollectionTypeNames(type, name)}
                      hint="Files in this folder are analysed as one document."
                    />,
                    `${type}-heading`,
                  )}
                {typeFileItems.map(fileItem =>
                  renderItem(fileItem, isCombined, false),
                )}
              </React.Fragment>
            );
          })}
        </List>
      </div>
    );
  };

  return (
    <div>
      {!hideUploadArea && renderUploadArea()}
      {renderFileList()}
    </div>
  );
};

export default FileUploader;
